import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchApi } from '@/common/lib/fetchApi'
import type { Timesheet } from './useTimesheets'

export function useTimesheetDetail(timesheetId: number | undefined) {
  return useQuery({
    queryKey: ['timesheet', timesheetId],
    queryFn: () => fetchApi<Timesheet>(`/api/timesheets/${timesheetId}`),
    enabled: !!timesheetId,
  })
}

export function useApproveTimesheet(timesheetId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      fetchApi<Timesheet>(`/api/timesheets/${timesheetId}/approve`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] })
      queryClient.invalidateQueries({ queryKey: ['timesheet', timesheetId] })
    },
  })
}

export async function downloadTimesheetsCsv(payPeriodId: number) {
  const { getMockTimeOverride } = await import('@/common/hooks/useMockTime')
  const { getImpersonationOverride } = await import('@/common/hooks/useImpersonation')
  const headers: HeadersInit = {}
  const mockTime = getMockTimeOverride()
  if (mockTime) headers['X-Mock-Time'] = mockTime
  const impersonateId = getImpersonationOverride()
  if (impersonateId) headers['X-Impersonate-Employee-Id'] = impersonateId

  const res = await fetch(`/api/export/timesheets?pay_period_id=${payPeriodId}`, {
    credentials: 'include',
    headers,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || `Export failed: ${res.status}`)
  }
  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition')
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] ?? 'timesheets.csv'
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function useRejectTimesheet(timesheetId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) =>
      fetchApi<Timesheet>(`/api/timesheets/${timesheetId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] })
      queryClient.invalidateQueries({ queryKey: ['timesheet', timesheetId] })
    },
  })
}

/**
 * Admin-only: send a submitted or approved timesheet back to the employee for
 * correction. The timesheet lands in `rejected`, so the employee edits and
 * resubmits and their manager re-approves through the normal path.
 */
export function useReopenTimesheet() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ timesheetId, reason }: { timesheetId: number; reason: string }) =>
      fetchApi<Timesheet>(`/api/timesheets/${timesheetId}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_data, { timesheetId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['approvals'] })
      queryClient.invalidateQueries({ queryKey: ['timesheet', timesheetId] })
    },
  })
}
